import React from 'react';
import Image from 'gatsby-image';
import {
  ContentParagraph,
  StyledUl,
  StyledListItem,
  ContactLink,
} from 'components/Typography';
import {
  ColorPageContainer,
  ContentContainer,
  PhotosContainer,
  ImageContainer,
  Video,
  VideoContainer,
  AudiobookContainer,
} from 'components/Containers';
import { Subheading } from 'components/Parallax/Subheading';
import { Hero } from 'components/Hero';
import { ScrollTopButton } from 'components/ScrollTopButton';
import { SmallText } from 'components/Hero/Title/styles';

// Images Hooks
import { useTicketsImages } from 'shared/Hooks/Tickets';
import { useExhibitionsImages } from 'shared/Hooks/Exhibitions';
import { useConcertsImages } from 'shared/Hooks/Concerts';
import { useVRImages } from 'shared/Hooks/VR';
import { usePerformancesImages } from 'shared/Hooks/Performances';
import { useSchoolImages } from 'shared/Hooks/School';
import { useAudiobooksImages } from 'shared/Hooks/Audiobooks';
import { useFoundationImages } from 'shared/Hooks/Foundation';
import { useContactImages } from 'shared/Hooks/Contact';

// VR
import vrVideo from 'assets/video/vr_background.mp4';

// Audiobooks
import { AudiobookLabel } from 'components/Typography';

export const usePagesContent = () => {
  const ticketPhotos = useTicketsImages();
  const exhibPhotos = useExhibitionsImages();
  const concertsPhotos = useConcertsImages();
  const vrPhotos = useVRImages();
  const performPhotos = usePerformancesImages();
  const schoolPhotos = useSchoolImages();
  const audiobooksPhotos = useAudiobooksImages();
  const foundationPhotos = useFoundationImages();
  const contactPhotos = useContactImages();
  const ticketHeroPhotos = {
    first:
      <Image fluid={ticketPhotos._001_small.childImageSharp.fluid}/>,
    second:
      <Image fluid={ticketPhotos._002_small.childImageSharp.fluid}/>,
    third:
      <Image fluid={ticketPhotos._003_small.childImageSharp.fluid}/>,
  };

  const exhibHeroPhotos = {
    first:
      <Image fluid={exhibPhotos._001_small.childImageSharp.fluid}/>,
    second:
      <Image fluid={exhibPhotos._002_small.childImageSharp.fluid}/>,
    third:
      <Image fluid={exhibPhotos._003_small.childImageSharp.fluid}/>,
  };

  const concertsHeroPhotos = {
    first:
      <Image fluid={concertsPhotos._001_small.childImageSharp.fluid}/>,
    second:
      <Image fluid={concertsPhotos._002_small.childImageSharp.fluid}/>,
    third:
      <Image fluid={concertsPhotos._003_small.childImageSharp.fluid}/>,
  };

  const vrHeroPhotos = {
    first:
      <Image fluid={vrPhotos._001_small.childImageSharp.fluid}/>,
    second:
      <Image fluid={vrPhotos._002_small.childImageSharp.fluid}/>,
    third:
      <Image fluid={vrPhotos._003_small.childImageSharp.fluid}/>,
  };

  const performHeroPhotos = {
    first:
      <Image fluid={performPhotos._001_small.childImageSharp.fluid}/>,
    second:
      <Image fluid={performPhotos._002_small.childImageSharp.fluid}/>,
    third:
      <Image fluid={performPhotos._003_small.childImageSharp.fluid}/>,
  };

  const schoolHeroPhotos = {
    first:
      <Image fluid={schoolPhotos._001_small.childImageSharp.fluid}/>,
    second:
      <Image fluid={schoolPhotos._002_small.childImageSharp.fluid}/>,
    third:
      <Image fluid={schoolPhotos._003_small.childImageSharp.fluid}/>,
  };

  const audiobooksHeroPhotos = {
    first:
      <Image fluid={audiobooksPhotos._001_small.childImageSharp.fluid}/>,
    second:
      <Image fluid={audiobooksPhotos._002_small.childImageSharp.fluid}/>,
    third:
      <Image fluid={audiobooksPhotos._003_small.childImageSharp.fluid}/>,
  };

  const foundationHeroPhotos = {
    first:
      <Image fluid={foundationPhotos._001_small.childImageSharp.fluid}/>,
    second:
      <Image fluid={foundationPhotos._002_small.childImageSharp.fluid}/>,
    third:
      <Image fluid={foundationPhotos._003_small.childImageSharp.fluid}/>,
  };

  const contactHeroPhotos = {
    second:
      <Image fluid={contactPhotos._002_small.childImageSharp.fluid}/>,
  };

  const pastEvents = [
    {
      date: '10 Listopada 2019',
      day: 'Niedziela',
      hour: '17:45',
      name: 'Świat Mistrza Beksińskiego/Koncert/Sold Out',
      place: 'Auditorium Maximum Uniwersytetu Jagiellońskiego/Kraków',
    },
    {
      date: '08 Sierpnia - 31 Sierpnia 2019',
      day: '',
      hour: '9:00-17:00',
      name:
        'Human Condition - Beksiński Multimedia Exhibition/Wystawa multimedialna',
      place: 'Muzeum Historyczne w Sanoku',
    },
    {
      date: '13 Kwietnia - 02 Czerwca 2019',
      day: '',
      hour: '12:00 - 20:00',
      name: 'Beksiński - od Fotografii do Wirtualnej Rzeczywistości/Wystawa',
      place: 'Centrum Praskie Koneser/Warszawa',
    },
    {
      date: '04 Marca 2019',
      day: '',
      hour: '17:45 i 20:45',
      name: 'Świat Mistrza Beksińskiego/Koncert/Sold Out',
      place: 'Filharmonia Narodowa/Warszawa',
    },
  ];

  const audiobooks = [
    {
      url:
        'https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/882846400&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true',
      label: (
        <AudiobookLabel>
          Fundacja Beksiśnki &#183; Zdzislaw Beksinski - "Wilki" czyta Rafał
          Szałajko
        </AudiobookLabel>
      ),
    },
    {
      url:
        'https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/882843133&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true',
      label: (
        <AudiobookLabel>
          Fundacja Beksiśnki &#183; Zdzisław Beksiński - "Śnieg" czyta Adam
          Biedrzycki
        </AudiobookLabel>
      ),
    },
    {
      url:
        'https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/882842107&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true',
      label: (
        <AudiobookLabel>
          Fundacja Beksiśnki &#183; Zdzislaw Beksinski - "Bakterie" czyta Rafał
          Szałajko
        </AudiobookLabel>
      ),
    },
    {
      url:
        'https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/882841156&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true',
      label: (
        <AudiobookLabel>
          Fundacja Beksiśnki &#183; Zdzisław Beksiński - "Na końcu ogrodu" czyta
          Rafał Szałajko
        </AudiobookLabel>
      ),
    },
    {
      url:
        'https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/882864448&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true',
      label: (
        <AudiobookLabel>
          Fundacja Beksiśnki &#183; Zdzisław Beksiński - "Zamach" czyta Rafał
          Szałajko
        </AudiobookLabel>
      ),
    },
    {
      url:
        'https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/882865906&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true',
      label: (
        <AudiobookLabel>
          Fundacja Beksiśnki &#183; Zdzislaw Beksinski - "Podpalacz" czyta Rafał
          Szałajko
        </AudiobookLabel>
      ),
    },
  ];
  const data = [
    {
      // TODO:
      // https://gitlab.com/interactivevision/beksinski/-/blob/711226f2ce4d0f1e6fe8e7c56b5e2d8cb10756ca/pages/bilety.vue#L192
      title: 'Bilety',
      seoTitle: 'Sprzedaż biletów - Fundacja Beksiński',
      seoDescription: 'Kup bilety na eventy organizowane przez Fundację Beksiński.',
      jsx: (
        <>
          <Hero
            bigTitle="Bilety"
            outlineTitle="Wystawy Wystawy"
            imagesHook={ticketHeroPhotos}
          />
          <ColorPageContainer pageColor="#fff">
            <Subheading color="#FFA290">Bilety Bilety Bilety</Subheading>
            <ContentContainer style={{ maxWidth: 'unset' }}>
              <div className="goOutBox">
                <div className="container">
                  <ContentParagraph typeTitle fontSize="35px">
                    Najbliższe wydarzenia
                  </ContentParagraph>
                  <div className="section__second--events">
                    <div id="accordion" className="accordion"/>
                    <div className="section__second--head m__top--100">
                      <ContentParagraph typeTitle fontSize="35px">
                        Archiwum wydarzeń
                      </ContentParagraph>
                    </div>
                    <div className="accordion"/>
                    {pastEvents.map((event, index) => (
                      <div key={index} className="goout-holder">
                        <div className="line"/>
                        <div className="goout pastEvent">
                          <div className="date">
                            <div className="day">
                              <span>{event.date}</span>
                              <span style={{ color: 'rgb(255, 162, 144)' }}>
                                {event.day}
                              </span>
                            </div>
                            <div className="hour">
                              <span>{event.hour}</span>
                            </div>
                          </div>
                          <div className="name">
                            <span>{event.name}</span>
                          </div>
                          <div className="date">
                            <span className="place">{event.place}</span>
                          </div>
                          <div className="formBtn ticket button goOutButton event_table--buy bold">
                            <a
                              name="Submit"
                              className="boxlink black-on-hover goOutBtn"
                            >
                              <span>KUP BILET</span>
                              <span className="event_table-cart button cart">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  xmlnsXlink="http://www.w3.org/1999/xlink"
                                  width="20"
                                  height="20"
                                  viewBox="0 0 20 20"
                                >
                                  <image
                                    width="20"
                                    height="20"
                                    xlinkHref="data:img/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAABaklEQVQ4jaXUTUtVQRzH8Y/iRpRwHQaG4SbEKKQ34MZF0EuwXkIkLtuGK3ciBEVQLXSjuBLEFhG4SGhT0RMqEQkuBB+KKG/8u3Pgcs/ccy/6hcPMmfnxPfOfM+d01Z7ODeCaOr/x2jnoSbKNBsUNbJ1VGcJ9LOMixvEQz0vJ9pxgKUouklH6Di6co+Lx7oabA8yXIp0T27TV3RSP5f46o/A+TpuFP/C4FG3PavFim4XBLP6WRlsT2ZliNifcxovSaGse4V2VMFgojeQ5woPGmZ5sjKnU/kxfT45jTGOvnfA67qa3fQXfS4kKcsLb6MIrjKSrHR/SCckKP6Z2Il2dcAdPWgmfoR8300o74XPVHtbwBX1YxG4pUWcUk1jHmyrhLayk/j0MZz7HS9hEL/4keexj9hxGqQXxSxvMZOIfGrJiUXEy/pMTRpmHqf8SXzOZGP+U+t+wVlXyW1zGUOqfZjLxwDFcxft0yME/zddGUqGZRBsAAAAASUVORK5CYII="
                                  />
                                </svg>
                              </span>
                            </a>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </ContentContainer>
            <ScrollTopButton/>
          </ColorPageContainer>
        </>
      ),
    },
    {
      title: 'Wystawy',
      images: useExhibitionsImages(),
      seoTitle: 'Wystawy - Fundacja Beksiński',
      seoDescription: 'Wystawy multimedialne składają się z pokazu przekroju twórczości Beksińskiego prezentowanego na wielkoformatowych ekranach.',
      jsx: (
        <>
          <Hero
            bigTitle="Wystawy"
            outlineTitle="Beksiński Beksiński"
            imagesHook={exhibHeroPhotos}
          />
          <ColorPageContainer pageColor="#FFA290">
            <Subheading>Nieznany Nieznany Nieznany</Subheading>
            <ContentContainer>
              <ContentParagraph>
                Wystawa multimedialna składająca się z pokazu przekroju
                twórczości Beksińskiego prezentowanego na wielkoformatowych
                ekranach. Pokaz połączony z muzyką i narracją, a także
                archiwalnymi wypowiedziami artysty. W 30-to minutowym widowisku
                multimedialnym, prezentowana jest wybrana twórczość Mistrza z
                dziedziny fotografii i malarstwa. Na tle twórczości
                prezentowanej podczas widowiska, pokazana została postać Artysty
                wypowiadającego się na temat: sztuki, filozofii, podejścia do
                twórczości.
              </ContentParagraph>
              <ContentParagraph>
                Integralną częścią wystawy jest również strefa Virtual Reality,
                gdzie za pomocą okularów VR widz może wejść “do środka obrazów”
                Beksińskiego i znaleźć się wewnątrz świata namalowanego przez
                mistrza.
              </ContentParagraph>
              <ContentParagraph>
                Kolejną atrakcję wystawy są obrazy w 3D. Dzieła Artysty można
                zobaczyć w technologii 3D (stereoskopia) zakładając odpowiednie
                okulary.
              </ContentParagraph>
              <ContentParagraph>
                Do stworzenia projektu zaangażowani zostali uznani artyści
                nowoczesnych sztuk wizualnych. Prezentacja prac w olbrzymich
                formatach, jednocześnie udźwiękowiona - ilustrowana odpowiednią
                muzyką, pozwala na dogłębną kontemplację dzieł z dbałością o
                każdy detal a także wyzwala nowe nieodkryte wcześniej emocje
                związane ze sztuką Artysty.
              </ContentParagraph>
              <ContentParagraph>
                Wystawę dopełniają dodatkowo filmy nakręcone kamerą VHS z
                prywatnych zbiorów artysty, a także wywiady i inne materiały
                Video z archiwów Fundacji Beksiński. Zdzisław Beksiński jako
                jeden z najwybitniejszych Polskich malarzy współczesnych
                zasługuje jako pierwszy na zaprezentowanie swojej twórczości w
                sposób nowoczesny i nowatorski. Tworząc ten projekt kontynuujemy
                niejako myśl twórczą samego Beksińskiego, dla którego
                wykorzystywanie nowych technologii na potrzeby sztuki było
                rzeczą naturalną.
              </ContentParagraph>
              <ContentParagraph>
                Całość składa się na niezwykle ciekawą i ekscytującą wystawę,
                która wychodzi naprzeciw oczekiwaniom odbiorców sztuki poprzez
                pobudzenie wielu zmysłów.
              </ContentParagraph>
            </ContentContainer>
            <ContentParagraph typeTitle>Zobacz zdjęcia</ContentParagraph>
            <PhotosContainer>
              <ImageContainer>
                <Image fluid={exhibPhotos._001_bottom.childImageSharp.fluid}/>
              </ImageContainer>
              <ImageContainer flexJustify="flex-end">
                <Image
                  imgStyle={{ objectFit: 'cover' }}
                  fluid={exhibPhotos._002_bottom.childImageSharp.fluid}
                />
              </ImageContainer>
              <ContentParagraph typeTitle>Zobacz video</ContentParagraph>
            </PhotosContainer>
          </ColorPageContainer>
          <VideoContainer>
            <Video src="https://player.vimeo.com/video/392684683"/>
            <ScrollTopButton/>
          </VideoContainer>
        </>
      ),
    },
    {
      title: 'Koncerty',
      seoTitle: 'Koncerty - Fundacja Beksiński',
      seoDescription: 'Koncerty z serii “Świat Mistrza Beksińskiego” - niezwykle wciągające i emocjonalne przeżycie audiowizualne.',
      jsx: (
        <>
          <Hero
            bigTitle="Koncerty"
            outlineTitle="Beksiński Beksiński"
            imagesHook={concertsHeroPhotos}
          />
          <ColorPageContainer pageColor="#F8DDF7">
            <Subheading>Koncerty Koncerty Koncerty</Subheading>
            <ContentContainer>
              <ContentParagraph>
                Wydarzenie artystyczne Świat Mistrza Beksińskiego stanowi
                wyjątkową propozycję dla odbiorców muzyki i malarstwa.
              </ContentParagraph>
              <ContentParagraph>
                Beksiński tworzył swoje obrazy w nieustającym kontakcie z
                muzyką. Wśród najczęściej wymienianych przez niego kompozytorów
                znajdował się Alfred Schnittke.
              </ContentParagraph>
              <ContentParagraph>
                Stąd też pomysł pokazania obrazów Beksińskiego w integralnym
                połączeniu z muzyką, co umożliwia widzowi możliwość pogłębionych
                wrażeń emocjonalnych.
              </ContentParagraph>
              <ContentParagraph>
                Kulminacyjną częścią koncertu jest wykonanie Requiem - A.
                Schnittkego połączone z prezentacją obrazów ściśle związaną z
                dramaturgią muzyki.
              </ContentParagraph>
              <ContentParagraph>
                Dodatkową atrakcją koncertu jest wystawa obrazów Beksińskiego
                pochodzących z Muzeum Historycznego w Sanoku (kilkanaście
                wybranych obrazów), która ustawiona jest w Foyer. Publiczność
                przed i w przerwie koncertu może obejrzeć wystawę oryginalnych
                obrazów mistrza.
              </ContentParagraph>
              <ContentParagraph>
                Koncert “Świat Mistrza Beksińskiego” jest przeżyciem niezwykle
                wciągającym i emocjonalnym. Zawsze spotyka się z bardzo
                pozytywnym odbiorem widzów i słuchaczy. Jest to wydarzenie inne
                niż wszystkie, promujące jednego z najwybitniejszych polskich
                malarzy, a także promujące sztukę wysoką podaną w taki sposób
                który jest bardzo atrakcyjny dla odbiorców. Jesteśmy przekonani,
                że koncert charakteryzujący się tym poziomem wykonawczym i
                artystycznym zdecydowanie wzbogaca ofertę kulturalną o sztukę
                wysoką tak bardzo potrzebną nam wszystkim.
              </ContentParagraph>
            </ContentContainer>
            <ContentParagraph typeTitle>Zobacz video</ContentParagraph>
          </ColorPageContainer>
          <VideoContainer>
            <Video src="https://player.vimeo.com/video/392691434"/>
            <ScrollTopButton/>
          </VideoContainer>
        </>
      ),
    },
    {
      title: 'Virtual Reality',
      seoTitle: 'Virtual Reality | Wirtualna rzeczywistość - Fundacja Beksiński',
      seoDescription: 'Przy pomocy okularów VR wejdziesz “do środka obrazów” Beksińskiego i zwiedzisz świat namalowany przez mistrza.',
      jsx: (
        <>
          <Hero
            bigTitle="Virtual Reality"
            outlineTitle="Virtual Virtual"
            videoUrl={vrVideo}
            photoUrl={vrPhotos._background.childImageSharp.fluid.src}
            type="vr"
            imagesHook={vrHeroPhotos}
          />
          <ColorPageContainer pageColor="#E1E7FF">
            <Subheading>Reality Reality Reality</Subheading>
            <ContentContainer>
              <ContentParagraph>
                Integralną częścią wystaw multimedialnych jest również strefa
                Virtual Reality, gdzie za pomocą okularów VR widz może wejść “do
                środka obrazów” Beksińskiego i znaleźć się wewnątrz świata
                namalowanego przez mistrza.
              </ContentParagraph>
              <ContentParagraph>
                Ta niezwykle atrakcyjna forma prezentacji sztuki pozwala na
                pełne zanurzenie w sztuce i eksplorację detali, zawartych w
                pracach mistrza. Wirtualna rzeczywistość przedstawiana jest jako
                komputerowo wykreowane przestrzenie, stworzone na bazie
                starannie wybranych na ten cel dzieł.
              </ContentParagraph>
              <ContentParagraph>
                Podczas doświadczenia VR, uczestnik zakłada specjalne okulary,
                które przenoszą go w wirtualny świat stworzony w oparciu o
                niezwykłą wyobraźnię artysty. Zestaw VR izoluje uczestnika od
                środowiska zewnętrznego, dzięki czemu podróż do wnętrza sztuki
                jest bardzo osobistym i bezpośrednim doświadczeniem.
              </ContentParagraph>
            </ContentContainer>
            <ContentParagraph typeTitle>Zobacz video</ContentParagraph>
          </ColorPageContainer>
          <VideoContainer>
            <Video src="https://player.vimeo.com/video/392678600"/>
            <ScrollTopButton/>
          </VideoContainer>
        </>
      ),
    },
    {
      title: 'Spektakle',
      seoTitle: 'Spektakle - Fundacja Beksiński',
      seoDescription: 'Wywiad, którego nie będzie to spektakl teatralny powstały na podstawie tekstów Zdzisława Beksińskiego pochodzących z różnych lat jego życia.',
      jsx: (
        <>
          <Hero
            bigTitle="Spektakle"
            outlineTitle="Spektakle Spektakle"
            imagesHook={performHeroPhotos}
          />
          <ColorPageContainer pageColor="#F4F4F4">
            <Subheading>Spektakle Spektakle Spektakle</Subheading>
            <ContentContainer>
              <ContentParagraph>
                Wywiad, którego nie będzie to spektakl teatralny powstały na
                podstawie tekstów Zdzisława Beksińskiego pochodzących z różnych
                lat jego życia. Dzięki nim poznać można rozmaite sytuacje
                życiowe malarza i zbliżyć się do lepszego rozumienia jego
                twórczości. Skromna scenografia stworzona w oparciu o
                autentyczne „sprzęty” pochodzące z warszawskiego mieszkania
                artysty uzupełniona jest wyświetlanymi na ekranie fotografiami i
                filmami ilustrującymi treść rozmowy.
              </ContentParagraph>
              <ContentParagraph>
                Za punkt wyjścia spektaklu autor scenariusza obrał tajemniczy
                sms, który otrzymał w rok po śmierci Zdzisława Beksińskiego a
                pochodzący z telefonu artysty z treścią: „Zadzwoń”. Na tej
                kanwie dochodzi do rozmowy, niezwykłej rozmowy…
              </ContentParagraph>
            </ContentContainer>
            <ScrollTopButton/>
          </ColorPageContainer>
        </>
      ),
    },
    {
      title: 'Dla szkół',
      seoTitle: 'Projekt edukacyjny dla szkół - Fundacja Beksiński',
      seoDescription: '"Zdzisław Beksiński w Twojej szkole" to ogólnopolski projekt edukacyjny stworzony z myślą o uczniach szkół średnich. Wykłady organizowane są przez Fundację Beksiński przy współpracy z Muzeum Historycznym miasta Sanoka.',
      jsx: (
        <>
          <Hero
            bigTitle="Dla szkół"
            outlineTitle="Dla szkół Dla szkół"
            imagesHook={schoolHeroPhotos}
          />
          <ColorPageContainer pageColor="#BAE9D0">
            <Subheading>Dla szkół Dla szkół Dla szkół</Subheading>
            <ContentContainer>
              <ContentParagraph>
                "Zdzisław Beksiński w Twojej szkole" to ogólnopolski projekt
                edukacyjny stworzony z myślą o uczniach szkół średnich. Wykłady
                organizowane są przez Fundację Beksiński przy współpracy z
                Muzeum Historycznym miasta Sanoka.
              </ContentParagraph>

              <ContentParagraph>
                Ta niebanalna forma lekcji o życiu i twórczości jednego z
                czołowych, współczesnych artystów wprowadza młodych ludzi w
                świat Mistrza i pokazuje im, że sztuka ukryta na co dzień w
                muzealnych salach wcale nie musi być nudna.
              </ContentParagraph>

              <ContentParagraph>
                To nie tylko interaktywna forma obcowania z twórczością artysty,
                ale również możliwość poznania go poprzez wspomnienia
                prowadzących lekcję przedstawicieli naszej Fundacji, którzy
                mieli okazję nie tylko współpracować z Mistrzem, ale również
                znali go prywatnie.
              </ContentParagraph>

              <ContentParagraph>
                Możliwość poznania technik wykorzystywanych przez artystę,
                zgłębienie bogatej symboliki metafizycznych prac Zdzisława
                Beksińskiego a także współczesne, cyfrowe interpretacje i
                aranżacje jego dzieł, mogą stać się inspirującym początkiem
                artystycznej drogi dla niejednego młodego człowieka.
              </ContentParagraph>

              <ImageContainer flexJustify="center">
                <Image fluid={schoolPhotos._004_small.childImageSharp.fluid}/>
              </ImageContainer>

              <ContentParagraph>
                Projekt realizowany od czerwca do grudnia 2022 ma na celu prezentację twórczości Zdzisława Beksińskiego na tle innych twórców polskiej sztuki współczesnej. Oferta skierowana jest do młodzieży szkół średnich, z terenów województwa podkarpackiego, z ograniczonym dostępem do kultury, ma na celu zainteresowanie młodzieży współczesną sztuką wizualną na przykładzie twórczości Zdzisława Beksińskiego.
              </ContentParagraph>

              <ContentParagraph>
                Oferta w ramach projektu prezentowana będzie na terenie szkół województwa podkarpackiego w formie wystawy, wykładu, prezentacji multimedialnej oraz pokazu Wirtualnej Rzeczywistości.
                <br/>
                Projekt dofinansowano ze środków Ministra Kultury i Dziedzictwa Narodowego pochodzących z Funduszu Promocji Kultury.
              </ContentParagraph>

              <ImageContainer flexJustify="center">
                <Image
                  imgStyle={{ objectFit: 'cover' }}
                  fluid={schoolPhotos._001_big.childImageSharp.fluid}
                />
              </ImageContainer>
            </ContentContainer>
            <ScrollTopButton/>
          </ColorPageContainer>
        </>
      ),
    },
    {
      title: 'Audiobooki',
      seoTitle: 'Opowiadania Beksińskiego - Fundacja Beksiński',
      seoDescription: 'Przedstawiamy serię opowiadań Zdzisława Beksińskiego w formie audiobooków do odsłuchania na naszej stronie.',
      jsx: (
        <>
          <Hero
            bigTitle="Audiobooki"
            outlineTitle="Audiobooki Audiobooki"
            imagesHook={audiobooksHeroPhotos}
          />
          <ColorPageContainer pageColor="#CFE9FF">
            <Subheading>AUDIOBOOKI AUDIOBOOKI AUDIOBOOKI</Subheading>
            <ContentContainer>
              <ContentParagraph>
                ...Zdzisław Beksiński po kilku latach bycia abstrakcjonistą
                zrozumiał, że jest to nowy „akademizm" hamujący rozwój
                artystyczny i ograniczający swobodę wypowiedzi. Zrozumiał, że
                musi nadal szukać swojej drogi. W latach 1963-65 powstały jego
                teksty literackie. Można się zastanowić, czy i one – podobnie
                jak twórczość fotograficzna – nie były jakąś formą rekompensaty
                za niespełnione pragnienie realizowania filmów; czy przynajmniej
                niektórych z nich nie można by uznać za kryptoscenariusze. Na
                pewno były próbą sprawdzenia się w nowej dyscyplinie twórczości.
                Już z dystansu, na przełomie lat 70. i 80. minionego wieku,
                podczas pewnej dyskusji z zaprzyjaźnionym malarzem Tadeuszem
                Turkowskim Beksiński wspominał, że usiłował niegdyś pisać i
                zastanawiał się wówczas, kim powinien zostać: malarzem, czy
                pisarzem?
              </ContentParagraph>
              <ContentParagraph>
                Wybrał malarstwo, bo dopiero w tej aktywności twórczej mógł być
                wolny. Praca literacka wiązała się przecież z cenzorskimi
                cięciami, „dogadywaniem się" z wydawcami, poddawaniem się ocenom
                krytyków, walką o przydział papieru i tym podobnymi problemami,
                zaś pisanie do szuflady nie miało sensu.
              </ContentParagraph>
              <ContentParagraph mb0>
                Faktem jest, że w 1963 roku sanocki artysta nie do końca był
                pewien, co robić dalej. Abstrakcja wyczerpała swoją
                atrakcyjność, rzeźba stawała się coraz bardziej uciążliwa
                fizycznie, zostawały rysunki i szczególne zainteresowanie
                erotyką. Wystawa jego aktualnego dorobku w Starej Pomarańczarni
                w Warszawskich Łazienkach w 1964 roku stała się symbolicznym
                zamknięciem tamtych poszukiwań. Trzy lata po następnej
                Stołecznej prezentacji Beksiński sprzedał niemalże wszystkie
                pokazywane dzieła. Odtąd zainteresowanie odbiorców przekładające
                się na powodzenie rynkowe pozwoliło mu żyć wyłącznie z
                malarstwa.
              </ContentParagraph>
              <ContentParagraph sign fontSize="20px">
                Wiesław Banach
              </ContentParagraph>
              <ContentParagraph>
                Mamy zaszczyt zaprezentować państwu Opowiadania Beksińskiego w
                formie audiobooków. Dwa z nich przeczytane przez Andrzeja
                Seweryna skłoniły nas do zaprezentowania kolejnych w formie
                słuchowisk, które z przyjemnością Państwu udostępniamy.
              </ContentParagraph>
              <ContentParagraph>
                Na koniec pozwolimy sobie zapożyczyć slogan Piotra
                Dmochowskiego, który ma w zwyczaju tak kończyć korespondencję
                mailową skierowaną do Fundacji.
              </ContentParagraph>
              <ContentParagraph>
                "…Chwała Wielkiemu Wizjonerowi…"
              </ContentParagraph>
              <ContentParagraph>
                Dofinansowano ze Środków Narodowego Centrum Kultury w ramach
                programu “Kultura w Sieci”
              </ContentParagraph>
              <AudiobookContainer>
                <iframe
                  title='Fundacja Beksiśnki, Zdzislaw Beksinski - "Bakterie" czyta Rafał Szałajko'
                  src="https://player.vimeo.com/video/455222697"
                  width="100%"
                  height="360"
                  frameBorder="0"
                  allow="autoplay; fullscreen"
                  allowFullScreen="allowfullscreen"
                />
              </AudiobookContainer>
              <AudiobookContainer>
                {audiobooks.map((audiobook, index) => (
                  <>
                    <iframe
                      key={index}
                      title={audiobook.label}
                      width="100%"
                      height="166"
                      scrolling="no"
                      frameBorder="no"
                      allow="autoplay"
                      src={audiobook.url}
                    />
                    {audiobook.label}
                  </>
                ))}
              </AudiobookContainer>
              <Image
                imgStyle={{ objectFit: 'cover' }}
                fluid={audiobooksPhotos.dofkult.childImageSharp.fluid}
              />
            </ContentContainer>
            <ScrollTopButton/>
          </ColorPageContainer>
        </>
      ),
    },
    {
      title: 'O fundacji',
      seoTitle: 'O fundacji - Fundacja Beksiński',
      seoDescription: 'Fundacja Beksiński formalnie działa od 2006 roku. Jej celem jest promowanie twórczości wielkiego polskiego malarza Zdzisława Beksińskiego.',
      jsx: (
        <>
          <Hero
            bigTitle="O Fundacji"
            outlineTitle="O Fundacji O Fundacji"
            imagesHook={foundationHeroPhotos}
          />
          <ColorPageContainer pageColor="#fff">
            <Subheading color="#B6E8EF">
              O fundacji O fundacji O fundacji
            </Subheading>
            <ContentContainer>
              <ContentParagraph medium as="h1">
                Jak powstała Fundacja Beksiński?
              </ContentParagraph>
              <ContentParagraph>
                Fundacja Beksiński formalnie działa <span>od 2006 roku.</span>
              </ContentParagraph>
              <ContentParagraph>
                Jej celem jest promowanie twórczości wielkiego polskiego malarza
                Zdzisława Beksińskiego, a także Muzeum Historycznego w Sanoku
                (gdzie znajduje się największa kolekcja obrazów artysty). Jej
                założyciele, Państwo Janusz i Ewa Baryccy znali się osobiście z
                Mistrzem oraz jego rodziną przez kilkadziesiąt lat.
              </ContentParagraph>
              <ContentParagraph>
                Pierwszą wystawę Beksińskiego zorganizowali w 1987 r. w
                Rzeszowie jako osoby prywatne zafascynowane twórczością mistrza.
              </ContentParagraph>
              <ContentParagraph>
                Przez lata znajomości z Beksińskimi zgromadzili dziesiątki
                godzin unikalnych materiałów video - osobiście nakręconych
                wywiadów z artystą i rozmów o sztuce i życiu. Te unikalne
                archiwalia również są wykorzystywane w produkcjach Fundacji
                promujących twórczość mistrza.
              </ContentParagraph>
              <>
                <ContentParagraph medium as="h3">
                  Od 2006 roku Fundacja Beksiński promuje twórczość Zdzisława
                  Beksińskiego poprzez trzy główne projekty:
                </ContentParagraph>
                <div>
                  <ContentParagraph>1. Wystawy multimedialne.</ContentParagraph>
                  <ContentParagraph>
                    2. Koncerty “Świat Mistrza Beksińskiego”.
                  </ContentParagraph>
                  <ContentParagraph>
                    2. Działalność edukacyjną w szkołach – wykłady multimedialne
                    dla młodzieży.
                  </ContentParagraph>
                </div>
              </>
              <>
                <ContentParagraph medium as="h4">
                  Celem fundacji jest:
                </ContentParagraph>
                <StyledUl>
                  <StyledListItem>
                    Ochrona dorobku artystycznego Zdzisława Beksińskiego.
                  </StyledListItem>
                  <StyledListItem>
                    Kultywowanie pamięci o Zdzisławie Beksińskim i jego
                    rodzinie.
                  </StyledListItem>
                  <StyledListItem>
                    Prowadzenie społecznej kontroli nad pozostawionym przez
                    artystę majątkiem dla Muzeum Historycznego w Sanoku.
                  </StyledListItem>
                  <StyledListItem>
                    Pomoc finansową w budowie muzeów poświęconych artyście.
                  </StyledListItem>
                  <StyledListItem>
                    Opieka nad miejscami pamięci po artyście i jego rodzinie
                    (np. nad grobowcem rodziny Beksińskich, pomnikami), oraz
                    tworzenie nowych miejsc pamięci.
                  </StyledListItem>
                  <StyledListItem>
                    Nabywanie prac artysty celem przekazywania ich Muzeum
                    Historycznemu w Sanoku.
                  </StyledListItem>
                  <StyledListItem>
                    Działania mające na celu zachowanie pamięci o Zdzisławie
                    Beksińskim.
                  </StyledListItem>
                  <StyledListItem>
                    Popularyzacja życia i dorobku artystycznego Zdzisława
                    Beksińskiego.
                  </StyledListItem>
                  <StyledListItem>
                    Prawna ochrona dobrego imienia Zdzisława Beksińskiego i jego
                    rodziny.
                  </StyledListItem>
                  <StyledListItem>
                    Założenie i utrzymanie strony internetowej poświęconej
                    Artyście.
                  </StyledListItem>
                </StyledUl>
              </>
            </ContentContainer>
            <ScrollTopButton/>
          </ColorPageContainer>
        </>
      ),
    },
    {
      title: 'Kontakt',
      seoTitle: 'Kontakt - Fundacja Beksiński',
      seoDescription: 'Skontaktuj się z nami aby dowiedzieć się więcej o naszej działalności eventowej.',
      jsx: (
        <>
          <Hero
            bigTitle="Kontakt"
            outlineTitle="Kontakt Kontakt"
            photoUrl={contactPhotos._001.childImageSharp.fluid.src}
            color="#ffa290"
            imagesHook={contactHeroPhotos}
          />
          <ColorPageContainer pageColor="#000" cursorWhite>
            <ContentContainer paddingContact>
              <SmallText contact>Zapraszamy do kontaktu</SmallText>
              <ContentParagraph medium color="#fff">
                Fundacja Beksiński <br/>
                ul.Kaktusowa 2<br/>
                35-604 Rzeszów
              </ContentParagraph>

              <ContentParagraph medium color="#fff">
                KRS 0000252981
                <br/>
                NIP: 813-345-02-49
                <br/>
                Konto nr:&nbsp;&nbsp; 75 1140 2017 0000 4002 0561 8220 MBank
              </ContentParagraph>

              <ContentParagraph typeTitle color="#fff">
                Tel:
              </ContentParagraph>

              <ContactLink href="tel:+48604295993">+48 604 295 993</ContactLink>

              <ContactLink href="tel:+48508182608">+48 508 182 608</ContactLink>

              <ContactLink href="tel:+48501138329">+48 501 138 329</ContactLink>

              <ContactLink href="tel:+48501138359">+48 501 138 359</ContactLink>

              <ContentParagraph typeTitle color="#fff">
                E-mail
              </ContentParagraph>

              <ContactLink href="mailto:fundacja@beksinski.com.pl">
                fundacja@beksinski.com.pl
              </ContactLink>

              <ContactLink href="mailto:fundacjabeksinski@gmail.com">
                fundacjabeksinski@gmail.com
              </ContactLink>

              <ContactLink href="mailto:michal.fundacjabeksinski@gmail.com">
                michal.fundacjabeksinski@gmail.com
              </ContactLink>
            </ContentContainer>
            <ScrollTopButton color={'white'}/>
          </ColorPageContainer>
        </>
      ),
    },
  ];

  return data;
};
